import {
  TextInput,
  CustomCheckbox,
  CustomSelect,
  CustomTooltip,
  RadioGroup,
  EthnicityDrawer,
} from '@/shared/ui/components';
import { riddleIcon, errorIcon } from '@/shared/ui/assets';
import { GENDER_OPTIONS } from '@/shared/config';
import { ETHNICITY_OPTIONS } from '@/entities/study';
import { isWebViewCreateStudy, gettextGender } from '@/shared/lib';
import _ from 'lodash';
import { forwardRef } from 'react';
import { t } from 'ttag';
import { handleKeyDown } from './handler';
import { useNewStudyStep1 } from './useNewStudyStep1';
import NewStudyStepSkeleton from '../newStudyStepSkeleton/newStudyStepSkeleton';
import type { NewStudyStep1Props } from './newStudyStep1.types';
import './newStudyStep1.scss';

/**
 * First step of the new study wizard collecting patient demographics. Returns NewStudyStepSkeleton until props.isLoadedData, otherwise renders the name, patient ID, masked date of birth, gender, height, weight, ethnicity and implantable-device fields bound to state.newStudyStep1Data with per-field errors, Enter-key navigation across the field id list, and a drawer explaining ethnicity.
 *
 * @param props - Step props including the loaded-data flag.
 * @param ref - Ref exposing the step imperative handle.
 * @returns The demographics step element, or the step skeleton while loading.
 */
const NewStudyStep1 = forwardRef((props: NewStudyStep1Props, ref) => {
  const { state, onChange, toggleEthnicityDrawer } = useNewStudyStep1(props, ref);

  const idArr = [
    'first-name',
    'middle-name',
    'last-name',
    'patient-id',
    'dob',
    'height-ft',
    'height-in',
    'weight',
    'ethnicity',
  ];

  // For responsive features: Case webview and resize events
  const isWebView = isWebViewCreateStudy();

  if (!props.isLoadedData) {
    return <NewStudyStepSkeleton />;
  }

  return (
    <div className="step-content step-1">
      <EthnicityDrawer
        open={state.isOpenEthnicityDrawer}
        onCancel={toggleEthnicityDrawer}
        width={500}
      />

      <div className="centered-content">
        <div className="centered-content__body">
          <TextInput
            id={idArr[0]}
            name="firstName"
            label={t`First name`}
            placeholder={t`Enter patient's first name`}
            value={state.newStudyStep1Data.firstName}
            onChange={onChange}
            isError={!!state.errorObject.firstName}
            errorMessage={state.errorObject.firstName}
            onKeyDown={(e) => handleKeyDown(e, idArr)}
          />
          <TextInput
            id={idArr[1]}
            name="middleName"
            label={
              <div>
                {t`Middle name`} <span className="optional-label">{`(${t`optional`})`}</span>
              </div>
            }
            placeholder={t`Enter patient's middle name`}
            value={state.newStudyStep1Data.middleName}
            onChange={onChange}
            isError={!!state.errorObject.middleName}
            errorMessage={state.errorObject.middleName}
            onKeyDown={(e) => handleKeyDown(e, idArr)}
          />
          <TextInput
            name="lastName"
            id={idArr[2]}
            label={t`Last name`}
            placeholder={t`Enter patient's last name`}
            value={state.newStudyStep1Data.lastName}
            onChange={onChange}
            isError={!!state.errorObject.lastName}
            errorMessage={state.errorObject.lastName}
            onKeyDown={(e) => handleKeyDown(e, idArr)}
          />
          <TextInput
            id={idArr[3]}
            name="patientId"
            label={t`Patient ID`}
            placeholder={t`Enter patient ID`}
            value={state.newStudyStep1Data.patientId}
            onChange={onChange}
            isError={!!state.errorObject.patientId}
            errorMessage={state.errorObject.patientId}
            onKeyDown={(e) => handleKeyDown(e, idArr)}
          />
          <TextInput
            id={idArr[4]}
            isDateMask
            name="dob"
            label={t`Date of birth`}
            placeholder={t`MM/DD/YYYY`}
            value={state.newStudyStep1Data.dob}
            onChange={onChange}
            isError={!!state.errorObject.dob}
            errorMessage={state.errorObject.dob}
            onKeyDown={(e) => handleKeyDown(e, idArr)}
            isKeyboardNumberMobile={isWebView}
          />
          <div>
            <span className="custom-text-input__label">{t`Gender`}</span>
            <div className="mt1">
              <RadioGroup
                className="radio-input"
                value={state.newStudyStep1Data.gender}
                name="gender"
                onChange={onChange}
                options={_.map(GENDER_OPTIONS, (item) => ({
                  label: gettextGender(item.label),
                  value: item.value,
                }))}
                size="small"
              />
            </div>
          </div>
          <div>
            <span className="custom-text-input__label">{t`Height`}</span>
            <div className="flex mt1 justify-between">
              <TextInput
                id={idArr[5]}
                name="heightFt"
                className="w-100 mr1"
                isNumberFormat
                value={state.newStudyStep1Data.heightFt}
                onChange={onChange}
                placeholder={t`Enter a number`}
                suffix="ft"
                onKeyDown={(e) => handleKeyDown(e, idArr)}
                decimalSeparator={false}
                isError={!!state.errorObject.heightFt}
                isKeyboardNumberMobile
              />
              <TextInput
                id={idArr[6]}
                name="heightIn"
                className="w-100 ml1"
                isNumberFormat
                value={state.newStudyStep1Data.heightIn}
                onChange={onChange}
                placeholder={t`Enter a number`}
                suffix="in"
                onKeyDown={(e) => handleKeyDown(e, idArr)}
                decimalSeparator={false}
                isError={!!state.errorObject.heightIn}
                isKeyboardNumberMobile
              />
            </div>
            {(!!state.errorObject.heightFt || !!state.errorObject.heightIn) && (
              <div className="flex items-center">
                <img className="icon-error" src={errorIcon} alt="Exclamation circle icon" />
                <div className="error-message">
                  {state.errorObject.heightFt || state.errorObject.heightIn}
                </div>
              </div>
            )}
          </div>
          <div>
            <TextInput
              id={idArr[7]}
              name="weight"
              label={t`Weight`}
              isNumberFormat
              value={state.newStudyStep1Data.weight}
              onChange={onChange}
              isError={!!state.errorObject.weight}
              errorMessage={state.errorObject.weight}
              placeholder={t`Enter a number (e.g., 176.5)`}
              suffix="lb"
              onKeyDown={(e) => handleKeyDown(e, idArr)}
              decimalScale={1}
              isKeyboardNumberMobile
            />
          </div>
          <div>
            <div className="optional-title">
              <button type="button" className="ethnicity-btn" onClick={toggleEthnicityDrawer}>
                <span className="title">{t`Ethnicity`}</span>
                <CustomTooltip title={t`Click to learn more about ethnicity`}>
                  <img src={riddleIcon} alt="riddleIcon" />
                </CustomTooltip>
              </button>
              <span className="optional-label">{`(${t`optional`})`}</span>
            </div>
            <CustomSelect
              id="ethnicity"
              placeholder={t`Select...`}
              name="ethnicity"
              size="middle"
              options={ETHNICITY_OPTIONS}
              showSearch
              allowClear
              value={state.newStudyStep1Data.ethnicity || null}
              onChange={onChange}
              aria-label={t`Ethnicity`}
            />
          </div>
          <div>
            <span className="custom-text-input__label">{t`Implantable device`}</span>
            <div className="implantable_device">
              <CustomCheckbox
                id="implantable_device__pacemaker"
                className="custom-checkbox"
                checked={state.newStudyStep1Data.pacer === 'Yes'}
                onChange={(checked) => {
                  onChange('pacer', checked ? 'Yes' : 'No');
                }}
                label={t`Pacemaker`}
              />
              <CustomCheckbox
                id="implantable_device__icd"
                className="custom-checkbox"
                checked={state.newStudyStep1Data.icd}
                onChange={(checked) => {
                  onChange('icd', checked);
                }}
                label={t`ICD`}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default NewStudyStep1;
